// One answer per tenant and user per page session for the setup surfaces (Suite
// Home panel, the "Einrichten" page, the sidebar entry): each booked module's
// GET /setup-entries keys, kept the way utils/statutoryRulesAccess.ts keeps its
// answer. Keyed on user AND tenant slug, so nobody is ever served another
// person's or workspace's answer; dropped on sign-out and whenever the tenants
// store re-reads itself (`forgetSetupEntries`), and a request that started
// before that never lands. A module that failed to answer leaves its section out
// of that one answer and the answer is NOT kept: the next surface that asks re-asks
// (no retry loop, no timer).
import { computed, onScopeDispose, ref, watch, type ComputedRef } from 'vue';
import { forgetSetupEntries, setupCache, type SetupAnswers } from './setupEntriesCache';
import { SETUP_SECTIONS, fetchSetupEntryKeys, resolveSetupSections, type SetupSection } from './setupPanel';

export { forgetSetupEntries };
const { answers, inflight } = setupCache;

const keyOf = (userId: string | null | undefined, tenantSlug: string) => `${userId ?? ''}\u0000${tenantSlug}`;

/**
 * Ask every BOOKED module (never a demo showroom one). `fresh` skips the kept
 * answer (the page re-reads on open) but still joins a request already in flight.
 */
export function loadSetupAnswers(
  userId: string | null | undefined,
  tenantSlug: string,
  bookedModuleKeys: string[],
  opts: { fresh?: boolean } = {},
): Promise<SetupAnswers> {
  const key = keyOf(userId, tenantSlug);
  if (!opts.fresh) {
    const kept = answers.get(key);
    if (kept) return Promise.resolve(kept);
  }
  const pending = inflight.get(key);
  if (pending) return pending;
  const startedIn = setupCache.generation;
  const booked = new Set(bookedModuleKeys);
  const request = Promise.all(
    SETUP_SECTIONS.map(async (s) => {
      const src = s.source;
      if (src.kind !== 'module' || !booked.has(src.moduleKey)) return null;
      return [src.moduleKey, await fetchSetupEntryKeys(src.client)] as const;
    }),
  ).then((settled) => {
    const asked = settled.filter((r) => r !== null);
    const out: SetupAnswers = {};
    let complete = true;
    for (const [moduleKey, keys] of asked) {
      if (keys === null) complete = false;
      else out[moduleKey] = keys;
    }
    if (startedIn === setupCache.generation) {
      inflight.delete(key);
      if (complete) answers.set(key, out);
    }
    return out;
  });
  inflight.set(key, request);
  return request;
}

/**
 * The sections the signed-in person may see, for one surface. Nothing is asked
 * until `eligible` holds; `sections` is empty (and `settled` false) until the
 * answers are in, so sections never pop in one by one. A person who is not
 * eligible settles to an empty answer once `ready` holds. A re-read for the same
 * person and workspace keeps the previous sections until the new answer lands;
 * anything else clears at once.
 */
export function useSetupSections(input: {
  eligible: () => boolean;
  userId: () => string | null | undefined;
  tenantSlug: () => string | null | undefined;
  bookedModules: () => string[];
  accessibleModules: () => string[];
  suiteAdmin: () => boolean;
  /** Bumps when the tenants store re-reads itself. */
  version: () => unknown;
  /**
   * The inputs above are final: the tenants and onboarding stores have loaded.
   * While false, an ineligible person is still "busy", not "nothing here" — the
   * answer may yet be eligible. Defaults to true (the panel and sidebar need no
   * settled state of their own).
   */
  ready?: () => boolean;
  fresh?: boolean;
}): { sections: ComputedRef<SetupSection[]>; settled: ComputedRef<boolean> } {
  const state = ref<SetupAnswers | null>(null);
  let run = 0;
  const stop = watch(
    () => [input.eligible(), input.userId(), input.tenantSlug(), input.version(), input.ready ? input.ready() : true] as const,
    ([ok, userId, slug, , ready], prev) => {
      const mine = ++run;
      const asking = ok && !!slug;
      const sameScope = !!prev && prev[0] && prev[1] === userId && prev[2] === slug;
      if (!asking) {
        // Nothing is asked; settled only once the inputs are final.
        state.value = ready ? {} : null;
        return;
      }
      if (!sameScope) state.value = null; // never one person's or workspace's sections for another
      void loadSetupAnswers(userId, slug, input.bookedModules(), { fresh: input.fresh }).then((a) => {
        if (mine === run) state.value = a; // a newer run (tenant switch, eligibility flip) owns the answer
      });
    },
    { immediate: true },
  );
  onScopeDispose(() => {
    run += 1;
    stop();
  });
  const sections = computed(() =>
    input.eligible() && state.value !== null
      ? resolveSetupSections(state.value, {
          suiteAdmin: input.suiteAdmin(),
          accessibleModules: input.accessibleModules(),
        })
      : [],
  );
  return { sections, settled: computed(() => state.value !== null) };
}
